import { useEffect, useRef, useState } from "react";
import ConnectMock from "./ConnectMock";
import KnowledgeMock from "./KnowledgeMock";
import ProfileMock from "./ProfileMock";
import FlowMock from "./FlowMock";
import "./how-dibs-works.css";
import "./feature-mockups.css";

const steps = [
  {
    id: "connect",
    anchor: "feature-inbox",
    label: "Step 1",
    title: "Connect Dibs to your existing stack",
    description:
      "Link the messaging apps, forms and booking tools you already use. Setup takes minutes, and there's nothing to replace or migrate.",
    mock: ConnectMock,
    alt: "Messaging apps, forms and booking tools that Dibs connects to",
  },
  {
    id: "learn",
    anchor: "feature-knowledge",
    label: "Step 2",
    title: "Teach Dibs your business",
    description:
      "Upload your SOPs, FAQs, offers and policies. Dibs reads and organises them into a knowledge base it can draw on.",
    mock: KnowledgeMock,
    alt: "Documents uploaded to Dibs and organised into a knowledge base",
  },
  {
    id: "profiles",
    anchor: "feature-profiles",
    label: "Step 3",
    title: "Let Dibs get to know every person",
    description:
      "Dibs pulls in contacts and bookings from the tools you connected, then combines them with your business knowledge to build a living profile of each person from every chat, form and booking.",
    mock: ProfileMock,
    alt: "Chats, bookings and forms building up a contact profile",
  },
  {
    id: "activate",
    anchor: "feature-workflows",
    label: "Step 4",
    title: "Activate workflows that use the context",
    description:
      "Dibs replies in one inbox and flags what needs a human. Trigger reminders, win-backs and check-ins from what people do and say, each one adapted to the person, not the average.",
    mock: FlowMock,
    alt: "A workflow that sends a personalised check-in after a visit",
  },
] as const;

export default function HowDibsWorks() {
  const listRef = useRef<HTMLOListElement>(null);
  const nodeRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const [reached, setReached] = useState(-1);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      list.style.setProperty("--how-progress", "1");
      setReached(steps.length - 1);
      return;
    }

    let frame = 0;
    const update = () => {
      frame = 0;
      const trigger = window.innerHeight * 0.55;
      const rect = list.getBoundingClientRect();
      const first = nodeRefs.current[0]?.getBoundingClientRect();
      const last = nodeRefs.current[steps.length - 1]?.getBoundingClientRect();
      if (!first || !last) return;
      const start = first.top + first.height / 2 - rect.top;
      const end = last.top + last.height / 2 - rect.top;
      // The line fills from the first node to the last as the trigger point passes them.
      const progress = Math.min(1, Math.max(0, (trigger - rect.top - start) / Math.max(1, end - start)));
      list.style.setProperty("--how-progress", progress.toFixed(4));
      list.style.setProperty("--how-start", `${start}px`);
      list.style.setProperty("--how-end", `${end}px`);
      let next = -1;
      nodeRefs.current.forEach((node, i) => {
        if (node && node.getBoundingClientRect().top + node.offsetHeight / 2 <= trigger) next = i;
      });
      setReached((prev) => (prev === next ? prev : next));
      // Lock scrolling to one step at a time while the section fills the screen.
      const section = list.closest("section")?.getBoundingClientRect();
      document.documentElement.classList.toggle("dh-how-snap", !!section && section.top <= 1 && section.bottom > 1);
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    update();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      document.documentElement.classList.remove("dh-how-snap");
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <section className="dh-section dh-how" aria-labelledby="dh-how-title">
      <div className="dh-container">
        <div className="dh-section-heading">
          <div className="dh-kicker">How Dibs works</div>
          <h2 id="dh-how-title">From first message to lasting relationship in four steps</h2>
          <p>Connect once, let Dibs learn your business, and put what it knows to work.</p>
        </div>

        <ol ref={listRef} className="dh-how-timeline">
          <span className="dh-how-spine" aria-hidden="true"><span className="dh-how-spine-fill" /></span>
          {steps.map((s, i) => (
            <li
              key={s.id}
              id={s.anchor}
              className={`dh-how-item${i % 2 ? " is-flipped" : ""}${i <= reached ? " is-reached" : ""}`}
            >
              <span ref={(node) => { nodeRefs.current[i] = node; }} className="dh-how-node" aria-hidden="true">{i + 1}</span>
              <div className="dh-how-card">
                <div className="dh-how-meta">{s.label}</div>
                <h3>{s.title}</h3>
                <p>{s.description}</p>
              </div>
              <div className="dh-how-shot" role="img" aria-label={s.alt}>
                <s.mock />
              </div>
            </li>
          ))}
        </ol>
      </div>
      <span className="dh-how-end" aria-hidden="true" />
    </section>
  );
}
